import { Immutable } from "immer";
import React, { useEffect, useRef, useState } from "react";

import { focusElement } from "../../utils/keyboardFocus";
import { FORM_CLASS_NAME, FORM_FIELD_HAS_ERRORS_SELECTOR } from "../classNames";

/** Focus the first invalid control in `form`, with NonFieldErrors-safe fallback. */
export const focusFirstFormError = (form: HTMLFormElement | null): void => {
    if (!form) {
        return;
    }
    // Prefer the first errored field that owns a control so an
    // input-less NonFieldErrors wrapper cannot swallow focus.
    const target =
        form.querySelector<HTMLElement>(
            FORM_FIELD_HAS_ERRORS_SELECTOR + " :is(input, select, textarea)",
        ) ??
        (form.querySelector(FORM_FIELD_HAS_ERRORS_SELECTOR)
            ? form.querySelector<HTMLElement>("input, select, textarea")
            : null);
    if (target) {
        focusElement(target, { focusVisible: true });
    }
};

export const Form = (
    props: Immutable<{
        id?: string;
        name?: string;
        className?: string;
        noValidate?: boolean;
        children?: React.ReactNode;
    }> & {
        onSubmit?: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
        ref?: React.RefObject<HTMLFormElement | null>;
    },
): React.ReactNode => {
    const {
        // A caller-supplied `className` replaces the default wholesale; callers
        // that want the module's `.form`-scoped label styling include
        // `FORM_CLASS_NAME` themselves.
        className = FORM_CLASS_NAME,
        children,
        onSubmit: onSubmitProp,
    } = props;
    const internalRef = useRef<HTMLFormElement>(null);
    const ref = props.ref || internalRef;
    const [submitLock, setSubmitLock] = useState(false);
    const [shouldResetInputFocus, setShouldResetInputFocus] = useState(false);

    // Handle focus reset after form submission
    useEffect(() => {
        if (shouldResetInputFocus) {
            focusFirstFormError(ref.current);
            setShouldResetInputFocus(false);
        }
    }, [shouldResetInputFocus, ref]);

    const onSubmit = async (evnt: React.FormEvent<HTMLFormElement>) => {
        evnt.preventDefault();
        if (submitLock) {
            console.log(
                "Ignoring form submit event because submitLock is active.",
            );
            return;
        }

        // Set latch to true so that later on, when useEffect is called (due to
        // error messages being added to the form), we can set the appropriate input
        // focus. The latch is needed to prevent changing focus after typing every character.
        setShouldResetInputFocus(true);

        if (onSubmitProp) {
            // Prevent multiple form submission
            setSubmitLock(true);
            try {
                await onSubmitProp(evnt);
            } catch (err) {
                console.error(err);
            }
            // Unlock form submission
            setSubmitLock(false);
            focusFirstFormError(ref.current);
        }
    };

    return (
        <form
            id={props.id}
            name={props.name}
            className={className}
            noValidate={props.noValidate}
            ref={ref}
            onSubmit={onSubmit}
        >
            {children}
        </form>
    );
};
